/**
 * Gallery Marquee — frontend behaviour.
 *
 * Reads the images from the block's core/gallery and builds two horizontal
 * tracks: the top row scrolls left→right (LTR), the bottom row right→left (RTL).
 * Both rows use the SAME images (cloned, so the browser still loads each file
 * once); each track holds two copies so GSAP can loop it seamlessly at ±50%.
 *
 * The editor (window.wp.data) is skipped — there the core/gallery just renders
 * as its normal grid so images stay editable. Reduced-motion users also keep
 * the static grid (we never build the marquee). The original gallery stays in
 * the DOM but sr-only once the marquee is built, so screen readers / crawlers
 * still get the real images and their alt text; the marquee rows are decorative.
 */

import { gsap } from "gsap";

const SECONDS_PER_IMAGE = 8; // loop pace scales with the gallery size

function buildTrack(images, modifier) {
  const track = document.createElement("div");
  track.className =
    `bskr-gallery-marquee__track bskr-gallery-marquee__track--${modifier}`;

  // Two copies so the track can loop seamlessly at a ±50% translate.
  for (let copy = 0; copy < 2; copy += 1) {
    images.forEach((img) => {
      const clone = img.cloneNode(true);
      clone.className = "bskr-gallery-marquee__img";
      clone.alt = "";
      clone.setAttribute("aria-hidden", "true");
      clone.loading = "lazy";
      clone.decoding = "async";
      // Keep the width/height attributes: they give the intrinsic aspect ratio
      // so `height:100%; width:auto` sizes the width correctly (even pre-load).
      track.appendChild(clone);
    });
  }
  return track;
}

// Rotate the array so the two rows don't start on the same image. Same set of
// images (loaded once), just a different starting offset — half-length gives the
// largest visual separation between the rows.
function rotate(arr, by) {
  const n = arr.length;
  if (n < 2) return arr.slice();
  const offset = ((by % n) + n) % n;
  return arr.slice(offset).concat(arr.slice(0, offset));
}

function buildRow(track) {
  const row = document.createElement("div");
  row.className = "bskr-gallery-marquee__row";
  row.appendChild(track);
  return row;
}

function initBlock(section) {
  if (section.dataset.galleryMarqueeInited === "1") return;

  const source = section.querySelector(".bskr-gallery-marquee__source");
  if (!source) return;

  const images = Array.from(source.querySelectorAll("img"));
  if (!images.length) return;

  // Reduced motion: leave the gallery as its static, editable grid.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  section.dataset.galleryMarqueeInited = "1";

  const rows = document.createElement("div");
  rows.className = "bskr-gallery-marquee__rows";
  rows.setAttribute("aria-hidden", "true");

  // Bottom row is offset by half so the two rows never start on the same image.
  const trackRtl = buildTrack(images, "rtl");
  const trackLtr = buildTrack(rotate(images, Math.floor(images.length / 2)), "ltr");
  // Top row scrolls RTL, bottom row LTR.
  rows.appendChild(buildRow(trackRtl));
  rows.appendChild(buildRow(trackLtr));
  section.appendChild(rows);

  // Keep the real gallery for SEO / screen readers, hide it visually.
  section.classList.add("is-marquee-ready");

  // One full loop scrolls past one copy (= the gallery once), so pacing the
  // duration by image count keeps a constant ~3s-per-image speed regardless of
  // how many images the editor added.
  const duration = images.length * SECONDS_PER_IMAGE;

  // LTR starts shifted one copy left and slides back to 0 (content moves right);
  // RTL starts at 0 and slides one copy left (content moves left). Both wrap
  // seamlessly because each track holds two identical copies.
  gsap.set(trackLtr, { xPercent: -50 });
  const tweenLtr = gsap.to(trackLtr, {
    xPercent: 0,
    ease: "none",
    duration,
    repeat: -1,
    paused: true,
  });
  const tweenRtl = gsap.to(trackRtl, {
    xPercent: -50,
    ease: "none",
    duration,
    repeat: -1,
    paused: true,
  });

  // Run only while the block is on screen.
  const io = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        tweenLtr.play();
        tweenRtl.play();
      } else {
        tweenLtr.pause();
        tweenRtl.pause();
      }
    },
    { threshold: 0 }
  );
  io.observe(section);
}

function init() {
  // Skip inside the Gutenberg editor — the gallery grid is the editor preview.
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;
  document.querySelectorAll(".bskr-gallery-marquee").forEach(initBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
